import React, { useState } from 'react';
import { Button } from './ui/button';
import { UsDateInput } from './ui/input';
import { isoToUsDate } from '../utils/dates';
import { ChevronDown } from 'lucide-react';

const PRESETS = [
  { id: 'today', label: 'Today' },
  { id: 'yesterday', label: 'Yesterday' },
  { id: 'wtd', label: 'WTD' },
  { id: 'mtd', label: 'MTD' },
  { id: 'last_month', label: 'Last Month' },
  { id: 'qtd', label: 'QTD' },
  { id: 'ytd', label: 'YTD' },
  { id: 'custom', label: 'Custom' },
];

function getDateRange(presetId) {
  const now = new Date();
  const y = now.getFullYear();
  const m = now.getMonth();
  const d = now.getDate();
  const pad = (n) => String(n).padStart(2, '0');
  const toISO = (dt) => `${dt.getFullYear()}-${pad(dt.getMonth() + 1)}-${pad(dt.getDate())}`;
  const today = toISO(now);
  // End-of-period helpers for "pipeline" lookahead: open loads scheduled to
  // deliver in the SAME period (not yet delivered) should still appear in
  // current-month / current-quarter / current-year views.
  const endOfMonth = (yr, mo) => toISO(new Date(yr, mo + 1, 0));
  const endOfQuarter = (yr, qi) => toISO(new Date(yr, qi * 3 + 3, 0));

  switch (presetId) {
    case 'today':
      return { start: today, end: today, pipelineEnd: today, label: `Today (${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` };
    case 'yesterday': {
      const yd = new Date(y, m, d - 1);
      const ydStr = toISO(yd);
      return { start: ydStr, end: ydStr, pipelineEnd: ydStr, label: `Yesterday (${yd.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` };
    }
    case 'wtd': {
      const dayOfWeek = now.getDay(); // 0=Sun
      const mondayOffset = dayOfWeek === 0 ? 6 : dayOfWeek - 1;
      const monday = new Date(y, m, d - mondayOffset);
      const sunday = new Date(y, m, d - mondayOffset + 6);
      return { start: toISO(monday), end: today, pipelineEnd: toISO(sunday), label: `WTD (${monday.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })} - ${now.toLocaleDateString('en-US', { month: 'short', day: 'numeric' })})` };
    }
    case 'mtd': {
      const start = `${y}-${pad(m + 1)}-01`;
      const monthName = now.toLocaleString('default', { month: 'short' });
      return { start, end: today, pipelineEnd: endOfMonth(y, m), label: `MTD (${monthName})` };
    }
    case 'last_month': {
      // Previous calendar month, fully closed (no pipeline lookahead — the
      // month is over). Handles Jan → Dec of prior year gracefully.
      const prevY = m === 0 ? y - 1 : y;
      const prevM = m === 0 ? 11 : m - 1;
      const start = `${prevY}-${pad(prevM + 1)}-01`;
      const endISO = endOfMonth(prevY, prevM);
      const monthName = new Date(prevY, prevM, 1).toLocaleString('default', { month: 'short' });
      return {
        start,
        end: endISO,
        pipelineEnd: endISO,
        label: `Last Month (${monthName} ${prevY})`,
      };
    }
    case 'qtd': {
      const q = Math.floor(m / 3);
      const qStart = `${y}-${pad(q * 3 + 1)}-01`;
      return { start: qStart, end: today, pipelineEnd: endOfQuarter(y, q), label: `QTD (Q${q + 1})` };
    }
    case 'ytd':
      return { start: `${y}-01-01`, end: today, pipelineEnd: `${y}-12-31`, label: `YTD ${y}` };
    default:
      return { start: `${y}-01-01`, end: today, pipelineEnd: `${y}-12-31`, label: `YTD ${y}` };
  }
}

export default function DateRangePicker({ onChange, defaultPreset = 'ytd' }) {
  const [active, setActive] = useState(defaultPreset);
  const [open, setOpen] = useState(false);
  const [customStart, setCustomStart] = useState('');
  const [customEnd, setCustomEnd] = useState('');

  const currentRange = active === 'custom'
    ? { start: customStart, end: customEnd, label: `${isoToUsDate(customStart)} to ${isoToUsDate(customEnd)}` }
    : getDateRange(active);

  const handlePreset = (id) => {
    if (id === 'custom') {
      setActive('custom');
      setOpen(true);
      return;
    }
    setActive(id);
    setOpen(false);
    const range = getDateRange(id);
    onChange?.(range.start, range.end, range.label, range.pipelineEnd);
  };

  const applyCustom = () => {
    if (customStart && customEnd) {
      setOpen(false);
      onChange?.(customStart, customEnd, `${isoToUsDate(customStart)} to ${isoToUsDate(customEnd)}`, customEnd);
    }
  };

  return (
    <div className="relative" data-testid="date-range-picker">
      <div className="flex items-center gap-0.5 bg-slate-100 p-1 rounded-lg overflow-x-auto whitespace-nowrap max-w-full scrollbar-hide">
        {PRESETS.map((p) => (
          <button
            key={p.id}
            onClick={() => handlePreset(p.id)}
            data-testid={`date-preset-${p.id}`}
            className={`px-2.5 py-1.5 rounded-md text-xs font-medium transition-all flex-shrink-0 ${
              active === p.id
                ? 'bg-white text-slate-900 shadow-sm'
                : 'text-slate-500 hover:text-slate-700 hover:bg-white/50'
            }`}
          >
            {p.label}
          </button>
        ))}
      </div>

      {/* Custom date inputs */}
      {open && active === 'custom' && (
        <div className="absolute top-full mt-2 right-0 z-50 bg-white border border-slate-200 rounded-lg shadow-lg p-4 space-y-3 min-w-[280px]" data-testid="custom-date-panel">
          <p className="text-xs font-medium text-slate-600">Custom Date Range</p>
          <div className="flex gap-2">
            <div className="flex-1">
              <label className="text-[10px] text-slate-500 block mb-1">Start</label>
              <UsDateInput
                value={customStart}
                onChange={(e) => setCustomStart(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs"
                data-testid="custom-start-date"
              />
            </div>
            <div className="flex-1">
              <label className="text-[10px] text-slate-500 block mb-1">End</label>
              <UsDateInput
                value={customEnd}
                onChange={(e) => setCustomEnd(e.target.value)}
                className="w-full border border-slate-200 rounded-md px-2 py-1.5 text-xs"
                data-testid="custom-end-date"
              />
            </div>
          </div>
          <div className="flex gap-2 justify-end">
            <Button size="sm" variant="outline" onClick={() => setOpen(false)} className="text-xs h-7">Cancel</Button>
            <Button size="sm" onClick={applyCustom} disabled={!customStart || !customEnd} className="text-xs h-7 bg-red-600 hover:bg-red-700" data-testid="apply-custom-date">Apply</Button>
          </div>
        </div>
      )}
    </div>
  );
}

export { getDateRange };
